import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldWarning, CheckCircle, X, CircleNotch, ArrowCounterClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

function OverrideModal({ person, item, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!note.trim()) { toast.error("Say why you're overriding — it goes on the record."); return; }
    setBusy(true);
    try {
      await api.post(`/users/${person.user_id}/compliance-override`, { item: item.key, action: "waive", note: note.trim() });
      toast.success(`Override saved for ${person.name} — the block lifts but the item keeps nagging until it's fixed.`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="override-modal">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-heading text-xl tracking-tight text-[#4A235A]">Override — {person.name}</p>
            <p className="mt-0.5 text-xs text-stone-500">{item.label}</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="override-close"><X size={18} /></button>
        </div>
        <p className="mt-3 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-3 text-xs text-stone-600">
          Overriding lifts the block so they can be scheduled — but this item <strong>keeps nagging</strong> in the
          daily digest until it's actually resolved. Your reason is audited.
        </p>
        <label className="mt-3 block">
          <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">Why are you overriding? *</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            placeholder="e.g. TB appointment booked for Friday — verified with the clinic"
            className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="override-note" />
        </label>
        <div className="mt-4 flex gap-2">
          <button onClick={save} disabled={busy}
            className="flex-1 rounded-full bg-[#4A235A] px-5 py-2 text-sm font-semibold text-white hover:bg-stone-900 disabled:opacity-50"
            data-testid="override-save">{busy ? "Saving…" : "Override (keep nagging)"}</button>
          <button onClick={onClose} className="rounded-full border border-stone-300 px-5 py-2 text-sm text-stone-600 hover:bg-stone-50" data-testid="override-cancel">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function ComplianceDirectoryPage() {
  const [people, setPeople] = useState(null);
  const [overrideFor, setOverrideFor] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/compliance-directory").then((r) => setPeople(r.data.people))
      .catch((e) => { toast.error(formatApiError(e)); setPeople([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const reinstate = async (person, item) => {
    try {
      await api.post(`/users/${person.user_id}/compliance-override`, { item: item.key, action: "clear" });
      toast.success(`Override removed — ${person.name} is blocked on this again.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!people) {
    return <div className="flex justify-center py-24"><CircleNotch size={28} className="animate-spin text-primary" /></div>;
  }

  const blockedPeople = people.filter((p) => p.blocked);
  const naggingOnly = people.filter((p) => !p.blocked);

  const PersonCard = ({ p }) => (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid={`dir-person-${p.user_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Link to={`/team/${p.user_id}`} className="font-heading text-lg tracking-tight text-[#4A235A] hover:underline" data-testid={`dir-name-${p.user_id}`}>
          {p.name}
        </Link>
        {p.employee_id && <span className="text-xs text-stone-400">#{p.employee_id}</span>}
        {p.blocked
          ? <span className="rounded-full border border-[#C0392B]/30 bg-[#C0392B]/5 px-2.5 py-0.5 text-xs font-semibold text-[#C0392B]">Blocked</span>
          : <span className="rounded-full border border-[#B7791F]/30 bg-[#B7791F]/5 px-2.5 py-0.5 text-xs font-semibold text-[#8a5d14]">Nagging only</span>}
      </div>
      <ul className="mt-3 space-y-2">
        {p.items.map((it) => (
          <li key={it.key} className="flex flex-wrap items-center gap-2 rounded-lg border border-stone-100 bg-stone-50/50 px-3 py-2" data-testid={`dir-item-${p.user_id}-${it.key}`}>
            <span className={`h-2 w-2 shrink-0 rounded-full ${it.effective_severity === "block" ? "bg-[#C0392B]" : "bg-[#B7791F]"}`} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-stone-800">{it.label}</p>
              {it.detail && <p className="text-xs text-stone-500">{it.detail}</p>}
              {it.overridden && (
                <p className="mt-0.5 text-[11px] font-medium text-[#2F6B4F]">
                  <CheckCircle size={11} className="mr-1 inline" />
                  Block overridden by {it.overridden.by} on {String(it.overridden.at || "").slice(0, 10)} — {it.overridden.note}
                </p>
              )}
            </div>
            {it.overridden ? (
              <button onClick={() => reinstate(p, it)}
                className="inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 py-1 text-[11px] font-semibold text-stone-600 hover:bg-stone-100"
                data-testid={`reinstate-${p.user_id}-${it.key}`}>
                <ArrowCounterClockwise size={11} /> Reinstate block
              </button>
            ) : it.effective_severity === "block" ? (
              <button onClick={() => setOverrideFor({ person: p, item: it })}
                className="rounded-full bg-[#4A235A] px-3 py-1 text-[11px] font-semibold text-white hover:bg-stone-900"
                data-testid={`override-${p.user_id}-${it.key}`}>
                Override
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8" data-testid="compliance-directory">
      <div>
        <h1 className="font-heading text-3xl tracking-tight text-[#4A235A]">Who needs what — the directory</h1>
        <p className="mt-1 text-sm text-stone-500">
          Everyone with an open compliance item, and exactly why. Override a block when you've handled it another way —
          it lifts the block but keeps nagging until the item is truly done.
        </p>
      </div>

      <div className="flex gap-3">
        <div className="flex-1 rounded-xl border border-[#C0392B]/20 bg-[#C0392B]/5 p-4 text-center" data-testid="dir-blocked-count">
          <p className="font-heading text-3xl font-semibold text-[#C0392B]">{blockedPeople.length}</p>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">blocked right now</p>
        </div>
        <div className="flex-1 rounded-xl border border-[#B7791F]/20 bg-[#B7791F]/5 p-4 text-center" data-testid="dir-nagging-count">
          <p className="font-heading text-3xl font-semibold text-[#8a5d14]">{naggingOnly.length}</p>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">open items, not blocking</p>
        </div>
      </div>

      {people.length === 0 && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="dir-all-clear">
          <CheckCircle size={32} weight="fill" className="mx-auto text-emerald-600" />
          <p className="mt-2 font-heading text-xl text-emerald-800">Everyone is clear</p>
          <p className="mt-1 text-sm text-emerald-700">No open compliance items across the roster.</p>
        </div>
      )}

      {blockedPeople.length > 0 && (
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#C0392B]"><ShieldWarning size={14} /> Blocked</p>
          {blockedPeople.map((p) => <PersonCard key={p.user_id} p={p} />)}
        </div>
      )}
      {naggingOnly.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">Open items — not blocking</p>
          {naggingOnly.map((p) => <PersonCard key={p.user_id} p={p} />)}
        </div>
      )}

      {overrideFor && (
        <OverrideModal person={overrideFor.person} item={overrideFor.item}
          onClose={() => setOverrideFor(null)} onSaved={() => { setOverrideFor(null); load(); }} />
      )}
    </div>
  );
}
